import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/Mp4Table.css";
import { useNavigate } from "react-router-dom";
import { videosAPI, fileAPI } from "../../api/index"; // adjust path to match your project

// Backend gives "YYYY-MM-DD" (Pydantic `date`). Display it the way the UI expects.
const formatDisplayDate = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return isoDate;
  return `${day}-${month}-${year}`;
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Extracts an 11-char YouTube video ID from watch/embed/short URLs.
const extractYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const match = url.match(re);
    if (match) return match[1];
  }
  return null;
};

const youtubeThumb = (url) => {
  const id = extractYoutubeId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
};

// Builds a proper /embed/ URL for the iframe. origin param helps avoid
// some embedding edge cases; rel=0 hides unrelated video suggestions.
const toYoutubeEmbedUrl = (url) => {
  const id = extractYoutubeId(url);
  if (!id) return null;
  return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
};

const PAGE_SIZE = 10;

function Mp4Table() {
  const navigate = useNavigate();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [dateFilter, setDateFilter] = useState("All Dates");
  const [currentPage, setCurrentPage] = useState(1);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- PREVIEW (in-page popup) ----------
  const [previewItem, setPreviewItem] = useState(null);
  const openPreview = (item) => setPreviewItem(item);
  const closePreview = () => setPreviewItem(null);

  // ---------- FETCH ----------
  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await videosAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setVideos(data);
    } catch (err) {
      console.error("Failed to load videos:", err);
      setError(err.response?.data?.detail || "Could not load videos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Close preview with Escape key
  useEffect(() => {
    if (!previewItem) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closePreview();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [previewItem]);

  const resetFilters = () => {
    setSearch("");
    setStatus("All Status");
    setDateFilter("All Dates");
    setCurrentPage(1);
  };

  // ---------- SEARCH + FILTER (client-side) ----------
  const filteredVideos = videos.filter((item) => {
    const searchLower = search.trim().toLowerCase();
    const matchesSearch =
      searchLower === "" ||
      (item.title || "").toLowerCase().includes(searchLower) ||
      (item.description || "").toLowerCase().includes(searchLower);

    const matchesStatus =
      status === "All Status" || (item.status || "").toLowerCase() === status.toLowerCase();

    let matchesDate = true;
    if (item.upload_date) {
      const [year, month] = item.upload_date.split("-").map(Number);
      const today = new Date();
      if (dateFilter === "This Month") {
        matchesDate = month === today.getMonth() + 1 && year === today.getFullYear();
      } else if (dateFilter === "This Year") {
        matchesDate = year === today.getFullYear();
      }
    }

    return matchesSearch && matchesStatus && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredVideos.length / PAGE_SIZE));
  const pageItems = filteredVideos.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  // ---------- EDIT LOGIC ----------
  const openEditModal = (item) => {
    setEditItem({ ...item });
    setEditFile(null);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditItem(null);
    setEditFile(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSourceChange = (source) => {
    setEditItem((prev) => ({ ...prev, video_source: source }));
    setEditFile(null);
  };

  const handleEditFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected) setEditFile(selected);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      let videoFileUrl = editItem.video_file_url;

      if (editItem.video_source === "upload" && editFile) {
        const uploadRes = await fileAPI.upload(editFile, "videos");
        videoFileUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url ||
          videoFileUrl;
      }

      await videosAPI.update(editItem.id, {
        title: editItem.title,
        video_source: editItem.video_source,
        youtube_url: editItem.video_source === "youtube" ? editItem.youtube_url : null,
        video_file_url: editItem.video_source === "upload" ? videoFileUrl : null,
        description: editItem.description || null,
        status: editItem.status,
      });

      await fetchVideos();
      closeEditModal();
    } catch (err) {
      console.error("Failed to update video:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  // ---------- DELETE LOGIC ----------
  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await videosAPI.delete(deleteId);
      await fetchVideos();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete video:", err);
      alert(err.response?.data?.detail || "Could not delete this video.");
    } finally {
      setDeleting(false);
    }
  };

  // ---------- STATS ----------
  const totalVideos = videos.length;
  const activeVideos = videos.filter((v) => v.status === "active").length;

  const thisMonth = videos.filter((v) => {
    if (!v.upload_date) return false;
    const today = new Date();
    const [year, month] = v.upload_date.split("-").map(Number);
    return month === today.getMonth() + 1 && year === today.getFullYear();
  }).length;

  return (
    <div className="vid123-page">
      <div className="vid123-header">
        <div>
          <h2 className="vid123-title">Video Gallery</h2>
          <p className="vid123-subtitle">Manage and organize all uploaded videos</p>
        </div>

        <button className="vid123-add-btn" onClick={() => navigate("/dashboard/mp4-form")}>
          <span className="vid123-add-icon">+</span> Add New Video
        </button>
      </div>

      {error && <div className="vid123-no-data">{error}</div>}

      <div className="vid123-stats-grid">
        <div className="vid123-stat-card vid123-stat-card-green">
          <div className="vid123-stat-icon vid123-stat-icon-green">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
          <div className="vid123-stat-info">
            <span className="vid123-stat-label">Total Videos</span>
            <span className="vid123-stat-value">{totalVideos}</span>
            <span className="vid123-stat-sub">All time videos</span>
          </div>
        </div>

        <div className="vid123-stat-card vid123-stat-card-blue">
          <div className="vid123-stat-icon vid123-stat-icon-blue">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M17 9l5-3v12l-5-3" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="vid123-stat-info">
            <span className="vid123-stat-label">Active Videos</span>
            <span className="vid123-stat-value">{activeVideos}</span>
            <span className="vid123-stat-sub">Currently active</span>
          </div>
        </div>

        <div className="vid123-stat-card vid123-stat-card-purple">
          <div className="vid123-stat-icon vid123-stat-icon-purple">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </div>
          <div className="vid123-stat-info">
            <span className="vid123-stat-label">This Month</span>
            <span className="vid123-stat-value">{thisMonth}</span>
            <span className="vid123-stat-sub">New videos added</span>
          </div>
        </div>
      </div>

      <div className="vid123-filters">
        <div className="vid123-search-box">
          <input
            type="text"
            placeholder="Search by video title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        <select
          className="vid123-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Status</option>
          <option>Active</option>
          <option>Inactive</option>
        </select>

        <select
          className="vid123-select"
          value={dateFilter}
          onChange={(e) => {
            setDateFilter(e.target.value);
            setCurrentPage(1);
          }}
        >
          <option>All Dates</option>
          <option>This Month</option>
          <option>This Year</option>
        </select>

        <button className="vid123-reset-btn" onClick={resetFilters}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v6h6M20 20v-6h-6M4.5 15a8 8 0 0014.5 3.5M19.5 9A8 8 0 005 5.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Clear Filters
        </button>
      </div>

      <div className="vid123-table-wrapper">
        <table className="vid123-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Video Details</th>
              <th>Source</th>
              <th>Status</th>
              <th>Upload Date</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="vid123-no-data">Loading...</td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan="6" className="vid123-no-data">No videos found.</td>
              </tr>
            ) : (
              pageItems.map((item, index) => {
                const thumb =
                  item.video_source === "youtube"
                    ? youtubeThumb(item.youtube_url)
                    : null;

                return (
                  <tr key={item.id}>
                    <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>

                    <td>
                      <div className="vid123-video-cell">
                        <button
                          type="button"
                          className="vid123-thumb-wrap vid123-thumb-btn"
                          onClick={() => openPreview(item)}
                          title="Play preview"
                        >
                          {thumb ? (
                            <img src={thumb} alt={item.title} className="vid123-thumb-img" />
                          ) : item.video_source === "upload" && item.video_file_url ? (
                            <video src={item.video_file_url} className="vid123-thumb-img" muted />
                          ) : (
                            <div className="vid123-thumb-img vid123-thumb-fallback" />
                          )}
                          <div className="vid123-thumb-overlay">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </div>
                        </button>

                        <div className="vid123-video-info">
                          <div className="vid123-video-title">{item.title}</div>
                          <div className="vid123-video-desc">{item.description}</div>
                          <button
                            type="button"
                            className="vid123-preview-trigger"
                            onClick={() => openPreview(item)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                            Preview video
                          </button>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="vid123-status-badge">
                        {item.video_source === "youtube" ? "YouTube" : "Uploaded"}
                      </span>
                    </td>

                    <td>
                      <span
                        className={`vid123-status-badge ${
                          item.status === "active" ? "vid123-status-active" : "vid123-status-inactive"
                        }`}
                      >
                        {capitalize(item.status)}
                      </span>
                    </td>

                    <td>
                      <div className="vid123-date-cell">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
                          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        <div>{formatDisplayDate(item.upload_date)}</div>
                      </div>
                    </td>

                    <td>
                      <button className="vid123-edit-btn" onClick={() => openEditModal(item)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Edit
                      </button>

                      <button className="vid123-delete-btn" onClick={() => openDeleteModal(item.id)}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="vid123-pagination-bar">
        <span className="vid123-pagination-info">
          Showing {pageItems.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1} to{" "}
          {(currentPage - 1) * PAGE_SIZE + pageItems.length} of {filteredVideos.length} videos
        </span>

        <div className="vid123-pagination-controls">
          <button className="vid123-page-arrow" onClick={() => handlePageChange(currentPage - 1)}>&lt;</button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              className={`vid123-page-num ${currentPage === p ? "vid123-active" : ""}`}
              onClick={() => handlePageChange(p)}
            >
              {p}
            </button>
          ))}

          <button className="vid123-page-arrow" onClick={() => handlePageChange(currentPage + 1)}>&gt;</button>
        </div>
      </div>

      {/* ---------------- PREVIEW POPUP (small, in-page) ---------------- */}
      {previewItem && (
        <div className="vid123-modal-overlay" onClick={closePreview}>
          <div
            className="vid123-modal vid123-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="vid123-modal-header">
              <h3>{previewItem.title}</h3>
              <button className="vid123-modal-close" onClick={closePreview}>
                &times;
              </button>
            </div>

            <div className="vid123-preview-body">
              {previewItem.video_source === "youtube" && toYoutubeEmbedUrl(previewItem.youtube_url) ? (
                <>
                  <div className="vid123-preview-frame-wrap">
                    <iframe
                      className="vid123-preview-frame"
                      src={toYoutubeEmbedUrl(previewItem.youtube_url)}
                      title={previewItem.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <p className="vid123-preview-fallback-note">
                    Video not loading?{" "}
                    <a href={previewItem.youtube_url} target="_blank" rel="noreferrer">
                      Open on YouTube instead
                    </a>{" "}
                    — some videos have embedding disabled by their owner, which YouTube
                    blocks at the video level and no site can override.
                  </p>
                </>
              ) : previewItem.video_source === "upload" && previewItem.video_file_url ? (
                <video
                  className="vid123-preview-video-el"
                  src={previewItem.video_file_url}
                  controls
                  autoPlay
                />
              ) : (
                <div className="vid123-no-data">No playable source for this video.</div>
              )}
            </div>

            {previewItem.description && (
              <p className="vid123-preview-desc">{previewItem.description}</p>
            )}
          </div>
        </div>
      )}

      {showEditModal && editItem && (
        <div className="vid123-modal-overlay" onClick={closeEditModal}>
          <div className="vid123-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vid123-modal-header">
              <h3>Edit Video</h3>
              <button className="vid123-modal-close" onClick={closeEditModal}>&times;</button>
            </div>

            <form className="vid123-modal-body" onSubmit={handleEditSave}>
              <div className="vid123-form-group">
                <label>Video Title</label>
                <input
                  type="text"
                  name="title"
                  value={editItem.title || ""}
                  onChange={handleEditChange}
                  required
                />
              </div>

              <div className="vid123-form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={editItem.description || ""}
                  onChange={handleEditChange}
                  rows="3"
                />
              </div>

              <div className="vid123-form-group">
                <label>Video Source</label>
                <div className="mp4-source-toggle">
                  <button
                    type="button"
                    className={`mp4-toggle-btn ${editItem.video_source === "youtube" ? "mp4-toggle-active" : ""}`}
                    onClick={() => handleEditSourceChange("youtube")}
                  >
                    YouTube URL
                  </button>
                  <button
                    type="button"
                    className={`mp4-toggle-btn ${editItem.video_source === "upload" ? "mp4-toggle-active" : ""}`}
                    onClick={() => handleEditSourceChange("upload")}
                  >
                    Upload Video
                  </button>
                </div>
              </div>

              {editItem.video_source === "youtube" ? (
                <div className="vid123-form-group">
                  <label>YouTube URL</label>
                  <input
                    type="text"
                    name="youtube_url"
                    value={editItem.youtube_url || ""}
                    onChange={handleEditChange}
                    required
                  />
                </div>
              ) : (
                <div className="vid123-form-group">
                  <label>Replace Video File (optional)</label>
                  <input type="file" accept=".mp4,video/*" onChange={handleEditFileChange} />
                  {editItem.video_file_url && (
                    <span className="mp4-file-info">Current file on record</span>
                  )}
                </div>
              )}

              <div className="vid123-form-group">
                <label>Status</label>
                <select name="status" value={editItem.status || "active"} onChange={handleEditChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="vid123-modal-footer">
                <button type="button" className="vid123-btn-cancel" onClick={closeEditModal} disabled={savingEdit}>
                  Cancel
                </button>
                <button type="submit" className="vid123-btn-save" disabled={savingEdit}>
                  {savingEdit ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="vid123-modal-overlay" onClick={closeDeleteModal}>
          <div className="vid123-modal vid123-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vid123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h3 className="vid123-confirm-title">Delete this video?</h3>
            <p className="vid123-confirm-text">
              This action cannot be undone. Are you sure you want to delete this video?
            </p>

            <div className="vid123-confirm-footer">
              <button className="vid123-btn-cancel" onClick={closeDeleteModal} disabled={deleting}>
                Cancel
              </button>
              <button className="vid123-btn-delete" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "OK, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Mp4Table;